import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const apiClient = axios.create({
  baseURL: API,
  withCredentials: true,
  headers: {
    'Content-Type': 'application/json',
  },
});

function getCookie(name) {
  const match = document.cookie.match(new RegExp('(^| )' + name + '=([^;]+)'));
  return match ? decodeURIComponent(match[2]) : null;
}

// Echoes the csrf_token cookie back as a header on every state-changing
// request, matching the backend's double-submit CSRF check. Safe methods
// (GET) don't need it - the backend only enforces it on POST/PUT/PATCH/DELETE.
apiClient.interceptors.request.use((config) => {
  const method = (config.method || 'get').toUpperCase();
  if (['POST', 'PUT', 'PATCH', 'DELETE'].includes(method)) {
    const csrfToken = getCookie('csrf_token');
    if (csrfToken) {
      config.headers['X-CSRF-Token'] = csrfToken;
    }
  }
  return config;
});

apiClient.interceptors.response.use(
  (response) => response,
  (error) => {
    // Only redirect on 401 if it's not an auth check request
    const isAuthCheck = error.config?.url?.includes('/auth/me');
    const isAdminCheck = error.config?.url?.includes('/admin/check');
    
    if (error.response?.status === 401 && !isAuthCheck && !isAdminCheck) {
      const currentPath = window.location.pathname;
      const publicPaths = ['/login', '/signup', '/', '/products', '/categories', '/about', '/community', '/faq', '/contact', '/privacy', '/terms', '/shipping', '/store-locator'];
      const isPublicPath = publicPaths.some(path => currentPath === path || currentPath.startsWith('/product/'));
      
      if (!isPublicPath) {
        window.location.href = '/login';
      }
    }
    return Promise.reject(error);
  }
);

// Auth API
// Sessions are cookie-based (httponly `session_token` set by the backend).
// There is no client-side token to manage - `withCredentials: true` above
// is what actually authenticates every request.
export const authAPI = {
  signup: async (email, password, name) => {
    const response = await apiClient.post('/auth/signup', {
      email,
      password,
      name
    });
    return response.data;
  },
  login: async (email, password) => {
    const response = await apiClient.post('/auth/login', {
      email,
      password
    });
    return response.data;
  },
  // Real Google / Facebook OAuth: these just build the backend's redirect
  // URL. The backend owns the entire OAuth code exchange server-side (see
  // backend/auth/) and redirects back to /auth/callback once the session
  // cookie has already been set - the frontend never sees a token.
  googleLoginUrl: (next = '/products') => `${API}/auth/google/login?next=${encodeURIComponent(next)}`,
  facebookLoginUrl: (next = '/products') => `${API}/auth/facebook/login?next=${encodeURIComponent(next)}`,
  verifyEmail: async (email, code) => {
    const response = await apiClient.post('/auth/verify-email', { email, code });
    return response.data;
  },
  resendVerification: async (email) => {
    const response = await apiClient.post('/auth/resend-verification', { email });
    return response.data;
  },
  getMe: async () => {
    const response = await apiClient.get('/auth/me');
    return response.data;
  },
  logout: async () => {
    const response = await apiClient.post('/auth/logout');
    return response.data;
  },
  updateProfile: async (data) => {
    const response = await apiClient.put('/auth/profile', data);
    return response.data;
  },
  updatePassword: async (currentPassword, newPassword) => {
    const response = await apiClient.put('/auth/password', {
      current_password: currentPassword,
      new_password: newPassword
    });
    return response.data;
  },
  updateProfilePicture: async (pictureUrl) => {
    const response = await apiClient.put('/auth/profile', { picture: pictureUrl });
    return response.data;
  },
  uploadProfilePicture: async (file) => {
    const formData = new FormData();
    formData.append('file', file);
    const response = await apiClient.post('/auth/profile/picture', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
    return response.data;
  },
};

// Products API
export const productsAPI = {
  getAll: async (params = {}) => {
    const response = await apiClient.get('/products', { params });
    return response.data;
  },
  getBrands: async () => {
    const response = await apiClient.get('/products/brands');
    return response.data;
  },
  getById: async (productId) => {
    const response = await apiClient.get(`/products/${productId}`);
    return response.data;
  },
  create: async (productData) => {
    const response = await apiClient.post('/products', productData);
    return response.data;
  },
  update: async (productId, productData) => {
    const response = await apiClient.put(`/products/${productId}`, productData);
    return response.data;
  },
  delete: async (productId) => {
    const response = await apiClient.delete(`/products/${productId}`);
    return response.data;
  },
};

// Cart API
export const cartAPI = {
  getCart: async () => {
    const response = await apiClient.get('/cart');
    return response.data;
  },
  addToCart: async (productId, quantity = 1) => {
    const response = await apiClient.post(`/cart?product_id=${productId}&quantity=${quantity}`);
    return response.data;
  },
  removeFromCart: async (productId) => {
    const response = await apiClient.delete(`/cart/${productId}`);
    return response.data;
  },
  updateQuantity: async (productId, quantity) => {
    const response = await apiClient.put(`/cart/${productId}?quantity=${quantity}`);
    return response.data;
  },
};

// Wishlist API
export const wishlistAPI = {
  getWishlist: async () => {
    const response = await apiClient.get('/wishlist');
    return response.data;
  },
  addToWishlist: async (productId) => {
    const response = await apiClient.post(`/wishlist?product_id=${productId}`);
    return response.data;
  },
  removeFromWishlist: async (productId) => {
    const response = await apiClient.delete(`/wishlist/${productId}`);
    return response.data;
  },
};

// Reviews API
export const reviewsAPI = {
  getReviews: async (productId) => {
    const response = await apiClient.get(`/reviews/${productId}`);
    return response.data;
  },
  createReview: async (reviewData) => {
    const response = await apiClient.post('/reviews', reviewData);
    return response.data;
  },
};

// Admin API
export const adminAPI = {
  getStats: async () => {
    const response = await apiClient.get('/admin/stats');
    return response.data;
  },
  getAllOrders: async () => {
    const response = await apiClient.get('/admin/orders');
    return response.data;
  },
  updateOrderStatus: async (orderId, status) => {
    const response = await apiClient.put(`/admin/orders/${orderId}/status?status=${status}`);
    return response.data;
  },
  getCustomers: async () => {
    const response = await apiClient.get('/admin/customers');
    return response.data;
  },
  getProducts: async () => {
    const response = await apiClient.get('/products');
    return response.data;
  },
  createProduct: async (productData) => {
    const response = await apiClient.post('/products', productData);
    return response.data;
  },
  updateProduct: async (productId, productData) => {
    const response = await apiClient.put(`/products/${productId}`, productData);
    return response.data;
  },
  deleteProduct: async (productId) => {
    const response = await apiClient.delete(`/products/${productId}`);
    return response.data;
  },
};

// Shipping API
export const shippingAPI = {
  getCities: async () => {
    const response = await apiClient.get('/shipping/cities');
    return response.data;
  },
  // Admin
  adminGetZones: async () => {
    const response = await apiClient.get('/admin/shipping/zones');
    return response.data;
  },
  adminCreateZone: async (zone) => {
    const response = await apiClient.post('/admin/shipping/zones', zone);
    return response.data;
  },
  adminUpdateZone: async (zoneKey, updates) => {
    const response = await apiClient.put(`/admin/shipping/zones/${zoneKey}`, updates);
    return response.data;
  },
  adminDeleteZone: async (zoneKey) => {
    const response = await apiClient.delete(`/admin/shipping/zones/${zoneKey}`);
    return response.data;
  },
  adminSetDefaultZone: async (zoneKey) => {
    const response = await apiClient.put(`/admin/shipping/zones/${zoneKey}/set-default`);
    return response.data;
  },
  adminGetCities: async () => {
    const response = await apiClient.get('/admin/shipping/cities');
    return response.data;
  },
  adminAddCity: async (city, zoneKey) => {
    const response = await apiClient.post('/admin/shipping/cities', { city, zone_key: zoneKey });
    return response.data;
  },
  adminUpdateCity: async (city, zoneKey) => {
    const response = await apiClient.put(`/admin/shipping/cities/${encodeURIComponent(city)}`, { zone_key: zoneKey });
    return response.data;
  },
  adminDeleteCity: async (city) => {
    const response = await apiClient.delete(`/admin/shipping/cities/${encodeURIComponent(city)}`);
    return response.data;
  },
};

// Orders API
export const ordersAPI = {
  create: async (orderData) => {
    const response = await apiClient.post('/orders', orderData);
    return response.data;
  },
  getAll: async () => {
    const response = await apiClient.get('/orders');
    return response.data;
  },
  getById: async (orderId) => {
    const response = await apiClient.get(`/orders/${orderId}`);
    return response.data;
  },
};

// Payment API
export const paymentAPI = {
  initiateMpesa: async (phoneNumber, orderId) => {
    const response = await apiClient.post(`/payments/mpesa/initiate?phone_number=${phoneNumber}&order_id=${orderId}`);
    return response.data;
  },
  getMpesaStatus: async (checkoutRequestId) => {
    const response = await apiClient.get(`/payments/mpesa/status/${checkoutRequestId}`);
    return response.data;
  },
  createStripeSession: async (orderId) => {
    const response = await apiClient.post(`/payments/stripe/session?order_id=${orderId}`, {}, {
      headers: {
        'origin': window.location.origin
      }
    });
    return response.data;
  },
  getStripeStatus: async (sessionId) => {
    const response = await apiClient.get(`/payments/stripe/status/${sessionId}`);
    return response.data;
  },
};

// Categories API
export const categoriesAPI = {
  getAll: async () => {
    const response = await apiClient.get('/categories');
    return response.data;
  },
};

// Recommendations API
export const recommendationsAPI = {
  getForProduct: async (productId) => {
    const response = await apiClient.get(`/recommendations/${productId}`);
    return response.data;
  },
};

// Content API (for admin-managed pages)
export const contentAPI = {
  getPage: async (pageId) => {
    const response = await apiClient.get(`/content/${pageId}`);
    return response.data;
  },
  updatePage: async (pageId, content) => {
    const response = await apiClient.put(`/content/${pageId}`, content);
    return response.data;
  },
  generateContent: async (prompt, pageType) => {
    const response = await apiClient.post('/content/generate', { prompt, page_type: pageType });
    return response.data;
  },
};

export default apiClient;
